import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "Client Work Productivity Analytics for Agencies | TeamGrid";
const SEO_DESCRIPTION = "Balance responsiveness and productivity with client work analytics, workload tracking and profitability insights for agencies and consultancies.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/client-driven-work";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const ClientDrivenWorkTeams = () => {
  const navigate = useNavigate();
  // FAQ data specific to Client-Driven Work Teams
  const clientDrivenWorkFAQs = [
    {
      question: "Does TeamGrid help manage client workload balance?",
      answer:
        "Yes, TeamGrid shows time spent on client tasks, helping balance workloads and improve client work productivity.",
    },
    {
      question: "Can TeamGrid improve focus on client deliverables?",
      answer:
        "Yes, by revealing unproductive interruptions, TeamGrid helps client teams improve focus and delivery quality.",
    },
    {
      question: "Is TeamGrid suitable for consulting firms?",
      answer:
        "Yes, TeamGrid supports consulting teams with insights into workload, productivity trends, and client responsiveness.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Does TeamGrid help manage client workload balance?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid shows how time is distributed across client tasks, helping teams balance workloads and improve client work productivity." } },
            { "@type": "Question", name: "Can TeamGrid improve focus on client deliverables?", acceptedAnswer: { "@type": "Answer", text: "Yes, by highlighting interruptions and fragmented work patterns, TeamGrid helps teams improve focus and delivery quality." } },
            { "@type": "Question", name: "Is TeamGrid suitable for consulting firms?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid supports consulting teams with insights into workload distribution, productivity trends, and client responsiveness." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="Client-Driven Work"
      heroTitle="Client Work Productivity Analytics for <span style='color: #00D1C1; font-weight: 600;'>Agencies & Consultancies</span>"
      heroSubtitle="Deliver Exceptional Client Service—Without Burning Out Your Team"
      primaryButtonText="Get a Demo for Client-Facing Teams"
      customPrimaryButton={customButton}
      fullSolutionContentTitle="Balance Responsiveness & Productivity"
      fullSolutionContentDescription="TeamGrid helps client-driven teams balance responsiveness, productivity, and sustainable performance through reactive vs proactive work analysis, client response monitoring, workload distribution tracking, and client profitability insights."
      fullSolutionContentOutcomes="Results include 28% improvement in client satisfaction, 40% increase in proactive work hours, 35% reduction in client-team turnover, and 32% improvement in client margins."
      hideProductivity={true}
      hideFeatures={true}
      hideOutcomes={true}
      hideWhyChoose={true}
        faqs={clientDrivenWorkFAQs}
        faqSubtitle="Everything you need to know about TeamGrid for Client-Driven Work Teams."
      />
    </>
  );
};

export default ClientDrivenWorkTeams;
